<script lang="ts">
  import Simple from "$app/Layouts/Simple.svelte"
  import { fly } from "svelte/transition"
  import { signupStore } from "$app/signupstore.svelte"
  import Free from "$app/Pages/Signup/Free.svelte"
  import Standard from "$app/Pages/Signup/Standard.svelte"
  import business_logo1 from "@images/companies/cba.png"
  import business_logo2 from "@images/companies/cfg-logo.png"
  import business_logo3 from "@images/companies/accutrans.png"
  import business_logo4 from "@images/companies/classic-plan.png"
  import business_logo5 from "@images/companies/horizon-hobby.png"
  import business_logo6 from "@images/companies/prevost.png"
  import { CheckCircle } from "lucide-svelte"
  import Video from "./Video.svelte"
  interface Props {
    stripe_publish_key: string
    recaptcha_site_key_v3: string
    selected_plan_id: string
    stripe_products: any[]
    params: any
    website_content: Partial<WebsiteContentType>
  }

  let {
    stripe_publish_key,
    recaptcha_site_key_v3,
    selected_plan_id,
    stripe_products,
    website_content
  }: Props = $props()

  signupStore.products = stripe_products
  signupStore.selectedPlan = signupStore.products.find(
    (p) => p.id === selected_plan_id
  )
  signupStore.billingInterval = "year"
  signupStore.stripe_publish_key = stripe_publish_key
</script>

<svelte:head>
  <script
    src="https://www.google.com/recaptcha/api.js?render={recaptcha_site_key_v3}"
    async
    defer
  ></script>
</svelte:head>

<Simple>
  {#snippet features(features: string[])}
    <ul class="mt-4 space-y-2">
      {#each features as feature}
        <li class="flex items-center justify-start">
          <CheckCircle class="text-green-500 size-5" />

          <span class="ml-3 font-medium text-gray-900">{feature}</span>
        </li>
      {/each}
    </ul>
  {/snippet}

  <div
    class="text-xl md:text-4xl lg:text-6xl font-extrabold text-center text-gray-600 max-w-7xl mx-auto mt-8"
  >
    {#if website_content?.signup_headline}
      {website_content.signup_headline}
    {:else}
      Join hundreds of other organizations who are building world-class E3
      cultures.
    {/if}
  </div>

  <div class="max-w-5xl mx-auto mt-6 md:mt-12 lg:mt-16">
    {#if selected_plan_id === "free"}
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div class="order-1 md:order-2">
          <Free {recaptcha_site_key_v3} />
        </div>
        <div class="px-4 order-2 md:order-1">
          <div class="bg-indigo-50 shadow rounded-b px-5 py-6">
            <p class="text-lg font-semibold text-tertiary">
              Our <span class="font-bold text-black">Free Subscription</span>
              includes the following limited features:
            </p>
            <ul class="mt-4 space-y-2">
              {#each signupStore.selectedPlan.features as feature}
                <li class="flex items-center justify-start">
                  <CheckCircle class="text-green-500 size-5" />

                  <span class="ml-3 text-base font-medium text-gray-900"
                    >{feature}</span
                  >
                </li>
              {/each}
            </ul>
            <p class="mt-6">
              <a
                href="/pricing"
                class="text-base text-blue-500 hover:text-blue-600 hover:underline"
              >
                Unlock all platform features with a Standard Subscription
              </a>
            </p>
            <div class="mt-4">
              <Video />
            </div>
          </div>
        </div>
      </div>
    {:else}
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Standard {recaptcha_site_key_v3} />
        <div class="order-2 md:order-1">
          <div class="bg-indigo-50 shadow rounded-b px-5 py-6">
            <p class="text-lg font-semibold text-tertiary">
              A <span class="font-bold text-black">Standard Subscription</span> unlocks
              all platform features, including:
            </p>
            {@render features(signupStore.selectedPlan.features)}
            <div class="mt-6 w-full flex justify-center">
              <a
                href="/pricing"
                class="text-sm text-blue-800 border rounded-full border-blue-100 py-2 px-4 bg-blue-200 hover:bg-blue-200"
              >
                Change Plan Type
              </a>
            </div>
            <div class="mt-4">
              <Video />
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>

  <div class="bg-white">
    <div class="max-w-7xl mx-auto py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
      <h2 class="text-3xl font-extrabold text-black text-center">
        Loved by Thousands of Businesses
      </h2>

      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
        <div class="flex justify-center items-center">
          <img src={business_logo1} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo2} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo3} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo4} class="mx-auto max-h-12 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo5} class="mx-auto max-h-10 w-auto" alt="" />
        </div>
        <div class="flex justify-center items-center">
          <img src={business_logo6} class="mx-auto max-h-16 w-auto" alt="" />
        </div>
      </div>
    </div>
  </div>

  {#if website_content?.show_recent_signups && website_content.recent_signups.length > 0}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <div
      class="hover:cursor-pointer fixed inset-x-0 left-0 bottom-20 px-6 pb-6 z-20"
      in:fly={{ y: 200, duration: 2000 }}
      onclick={() => {
        website_content.show_recent_signups = false
      }}
      role="button"
      aria-label="Close"
      tabindex="0"
    >
      <div
        class="pointer-events-auto max-w-xl rounded-xl bg-blue-600 py-8 px-6 shadow-lg ring-4 ring-blue-900/10"
      >
        <p class="text-xl leading-6 text-gray-100">
          {website_content.recent_signups[
            Math.floor(Math.random() * website_content.recent_signups.length)
          ]}
        </p>
      </div>
    </div>
  {/if}

  <style lang="css">
    #period-selector button {
      font-weight: 400;
      outline: none;
      transition: background-color 200ms ease-in-out;
    }
    #period-selector button.active {
      background-color: white;
      color: #222;
      font-weight: 700;
      z-index: 5;
    }
  </style>
</Simple>
